import React, { useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { MapPin, ExternalLink, Trash2, MoreVertical, Folder } from '@/ui/icons'
import { WaypointResponse } from '@/shared/api/routes'
import { cn } from '@/shared/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Checkbox,
} from '@/ui/components'

interface WaypointTreeItemProps {
  waypoint: WaypointResponse
  isSelected?: boolean
  onClick?: (waypoint: WaypointResponse) => void
  onAddToTrip?: (waypoint: WaypointResponse) => void
  onDelete?: (waypoint: WaypointResponse) => void
  onMoveToFolder?: (waypoint: WaypointResponse) => void
  depth?: number
  isDeleting?: boolean
  // Selection mode props
  selectionMode?: boolean
  isChecked?: boolean
  onToggleCheck?: (waypointId: string, checked: boolean) => void
}

export function WaypointTreeItem({
  waypoint,
  isSelected = false,
  onClick,
  onAddToTrip,
  onDelete,
  onMoveToFolder,
  depth = 0,
  isDeleting = false,
  selectionMode = false,
  isChecked = false,
  onToggleCheck,
}: WaypointTreeItemProps) {
  const [showActions, setShowActions] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `waypoint-${waypoint.id}`,
    data: {
      type: 'waypoint',
      waypoint,
    },
  })

  const style = {
    transform: CSS.Translate.toString(transform),
  }

  const handleClick = (e: React.MouseEvent) => {
    // In selection mode, toggle checkbox instead of selecting waypoint
    if (selectionMode) {
      if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
        return // Let checkbox handle its own click
      }
      onToggleCheck?.(waypoint.id, !isChecked)
      return
    }
    
    // Don't trigger onClick if clicking on actions
    if ((e.target as HTMLElement).closest('[data-action-button]')) {
      return
    }
    onClick?.(waypoint)
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleCheck?.(waypoint.id, e.target.checked)
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        marginLeft: `${depth * 16}px`, // 16px per level
      }}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors group',
        isDragging && 'opacity-50',
        selectionMode && isChecked && 'bg-adventure-50 dark:bg-adventure-900/30',
        !selectionMode && isSelected
          ? 'bg-adventure-100 text-adventure-900 dark:bg-adventure-900/40 dark:text-adventure-100'
          : !selectionMode &&
              'hover:bg-gray-100 dark:hover:bg-white/[0.06] text-gray-700 dark:text-foreground cursor-pointer',
        selectionMode && 'cursor-pointer',
        depth > 0 && `ml-${depth * 4}` // Dynamic indentation based on depth
      )}
      onClick={handleClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        // Don't hide actions if menu is open
        if (!isMenuOpen) {
          setShowActions(false)
        }
      }}
    >
      {/* Checkbox in selection mode */}
      {selectionMode && (
        <Checkbox
          checked={isChecked}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0"
        />
      )}

      {/* Drag handle (hidden in selection mode) */}
      {!selectionMode && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MapPin className="w-4 h-4" />
        </div>
      )}

      {/* Waypoint icon in selection mode */}
      {selectionMode && (
        <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      )}

      {/* Waypoint name */}
      <span className="flex-1 truncate">{waypoint.name || 'Unnamed Waypoint'}</span>

      {/* Action menu (hidden in selection mode, shown on hover or when open) */}
      {!selectionMode && (showActions || isMenuOpen) && (onClick || onAddToTrip || onDelete || onMoveToFolder) && (
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              data-action-button
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground rounded hover:bg-gray-200 dark:hover:bg-white/[0.08]"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {onAddToTrip && (
              <DropdownMenuItem onClick={() => {
                onAddToTrip(waypoint)
                setIsMenuOpen(false)
              }}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Add to Trip
              </DropdownMenuItem>
            )}
            {onMoveToFolder && (
              <DropdownMenuItem onClick={() => {
                onMoveToFolder(waypoint)
                setIsMenuOpen(false)
              }}>
                <Folder className="w-4 h-4 mr-2" />
                Move to Folder
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem
                onClick={() => {
                  onDelete(waypoint)
                  setIsMenuOpen(false)
                }}
                disabled={isDeleting}
                className="text-error"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

